import React, { useCallback, useEffect, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { PlusOutlined, SyncOutlined } from '@ant-design/icons';
import * as whatsappBroadcastService from '../../services/whatsappBroadcastService';
import { WATemplate, WATemplateHeaderType } from '../../types/whatsapp-broadcast';
import { useAuth } from '../../contexts/AuthContext';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const categoryOptions = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];
const headerTypeOptions: WATemplateHeaderType[] = ['none', 'text', 'image', 'document'];
const statusOptions = ['APPROVED', 'PENDING', 'REJECTED'];

const statusColor: Record<string, string> = {
  APPROVED: 'green',
  PENDING: 'orange',
  REJECTED: 'red',
};

const Templates: React.FC = () => {
  const { message } = App.useApp();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm();

  const canManage = hasPermission('wa.template.manage');

  const [templates, setTemplates] = useState<WATemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WATemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await whatsappBroadcastService.listTemplates();
      setTemplates(r.data || []);
    } catch (error) {
      console.error(error);
      message.error('Gagal memuat daftar template');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await whatsappBroadcastService.syncTemplates();
      const count = typeof r.data === 'number' ? r.data : r.data?.synced ?? r.data?.count;
      message.success(
        count !== undefined ? `Sinkronisasi berhasil, ${count} template tersinkron` : r.message || 'Sinkronisasi berhasil'
      );
      await fetchTemplates();
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal sinkronisasi template dari Meta');
    } finally {
      setSyncing(false);
    }
  };

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ language: 'id', status: 'APPROVED', header_type: 'none', body_param_count: 0 });
    setModalOpen(true);
  };

  const openEditModal = (template: WATemplate) => {
    setEditing(template);
    form.setFieldsValue({
      name: template.name,
      language: template.language,
      category: template.category,
      header_type: template.header_type,
      body_param_count: template.body_param_count,
      status: template.status,
      components: (template as any).components || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: any;
    try {
      values = await form.validateFields();
    } catch (error) {
      return; // validation errors are rendered inline
    }
    setSubmitting(true);
    try {
      if (editing) {
        await whatsappBroadcastService.updateTemplate(editing.id, values);
        message.success('Template berhasil diperbarui');
      } else {
        await whatsappBroadcastService.createTemplate(values);
        message.success('Template berhasil dibuat');
      }
      setModalOpen(false);
      await fetchTemplates();
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal menyimpan template');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleEnabled = async (record: WATemplate, checked: boolean) => {
    setTogglingId(record.id);
    try {
      await whatsappBroadcastService.setTemplateEnabled(record.id, checked);
      message.success(checked ? 'Template diaktifkan' : 'Template dinonaktifkan');
      await fetchTemplates();
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal mengubah status aktif template');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await whatsappBroadcastService.deleteTemplate(id);
      message.success('Template berhasil dihapus');
      await fetchTemplates();
    } catch (error: any) {
      console.error(error);
      message.error(error?.response?.data?.message || 'Gagal menghapus template');
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Template WhatsApp
          </Title>
        </Col>
        <Col>
          <Button icon={<SyncOutlined />} loading={syncing} onClick={handleSync} style={{ marginRight: 8 }}>
            Sync dari Meta
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Tambah Template
          </Button>
        </Col>
      </Row>

      <Card bordered={false}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={templates}
          pagination={{ pageSize: 10 }}
          columns={[
            { title: 'Nama', dataIndex: 'name' },
            { title: 'Bahasa', dataIndex: 'language' },
            { title: 'Kategori', dataIndex: 'category' },
            {
              title: 'Status',
              dataIndex: 'status',
              render: (status: string) => <Tag color={statusColor[status] || 'default'}>{status}</Tag>,
            },
            { title: 'Tipe Header', dataIndex: 'header_type' },
            { title: 'Jumlah Param Body', dataIndex: 'body_param_count' },
            {
              title: 'Aktif',
              dataIndex: 'enabled',
              render: (enabled: boolean, record: WATemplate) => (
                <Switch
                  checked={!!enabled}
                  disabled={!canManage}
                  loading={togglingId === record.id}
                  onChange={(checked) => handleToggleEnabled(record, checked)}
                />
              ),
            },
            {
              title: 'Aksi',
              key: 'action',
              render: (_: any, record: WATemplate) => (
                <>
                  <Button size="small" onClick={() => openEditModal(record)} style={{ marginRight: 8 }}>
                    Edit
                  </Button>
                  <Popconfirm
                    title="Hapus template ini?"
                    onConfirm={() => handleDelete(record.id)}
                    okText="Hapus"
                    cancelText="Batal"
                  >
                    <Button size="small" danger>
                      Hapus
                    </Button>
                  </Popconfirm>
                </>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title={editing ? 'Edit Template' : 'Tambah Template'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        destroyOnClose
        okText="Simpan"
        cancelText="Batal"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Nama Template" rules={[{ required: true, message: 'Nama wajib diisi' }]}>
            <Input disabled={!!editing} placeholder="promo_properti" />
          </Form.Item>
          <Form.Item name="language" label="Bahasa" rules={[{ required: true, message: 'Bahasa wajib diisi' }]}>
            <Input placeholder="id" />
          </Form.Item>
          <Form.Item name="category" label="Kategori" rules={[{ required: true, message: 'Pilih kategori' }]}>
            <Select placeholder="Pilih kategori">
              {categoryOptions.map((c) => (
                <Option key={c} value={c}>
                  {c}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="header_type" label="Tipe Header" rules={[{ required: true, message: 'Pilih tipe header' }]}>
            <Select placeholder="Pilih tipe header">
              {headerTypeOptions.map((h) => (
                <Option key={h} value={h}>
                  {h}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="body_param_count"
            label="Jumlah Parameter Body"
            rules={[{ required: true, message: 'Isi jumlah parameter' }]}
          >
            <InputNumber min={0} max={20} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Pilih status' }]}>
            <Select placeholder="Pilih status">
              {statusOptions.map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="components" label="Components (JSON, opsional)">
            <TextArea rows={4} placeholder='[{"type":"BODY","text":"..."}]' />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Templates;
